import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import ApplicationAppService from '../application/platform/application-app-service';

interface ErrorPageProps {
    code: string;
    title: string;
    message: string;
    icon: React.ReactNode;
    color: 'error' | 'warning';
    showBack?: boolean;
}

// Usuário autenticado volta à página inicial do módulo; os demais vão para o login
function getPrimaryAction() {
    if (!ApplicationAppService.getAuthenticationData()) {
        return { to: '/', label: 'Ir para o login', icon: <LoginIcon /> };
    }
    const { initialPage } = ApplicationAppService.getApplicationData();
    return { to: `/${initialPage ?? ''}`, label: 'Voltar ao início', icon: <HomeIcon /> };
}

const actionButtonSx = { minWidth: 200, fontWeight: 600, fontSize: '1rem', py: 1.5 };

export function ErrorPage({ code, title, message, icon, color, showBack }: ErrorPageProps) {
    const navigate = useNavigate();
    const primaryAction = getPrimaryAction();

    return (
        <Box
            component="main"
            sx={{
                backgroundColor: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                alignItems: 'center',
                display: 'flex',
                flexGrow: 1,
                minHeight: '100vh',
                py: 4
            }}
        >
            <Container maxWidth="md">
                <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            bgcolor: `${color}.main`,
                            color: `${color}.contrastText`,
                            mb: 3,
                            boxShadow: 3,
                            '& svg': { fontSize: 72 }
                        }}
                    >
                        {icon}
                    </Box>

                    <Typography variant="h1" sx={{ fontSize: { xs: '6rem', md: '8rem' }, fontWeight: 700, color: `${color}.main`, mb: 2, lineHeight: 1 }}>
                        {code}
                    </Typography>

                    <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        {title}
                    </Typography>

                    <Typography sx={{ color: 'text.secondary', fontSize: '1.1rem', mb: 4, maxWidth: 600, mx: 'auto', lineHeight: 1.7 }}>
                        {message}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
                        {showBack && (
                            <Button onClick={() => navigate(-1)} size="large" variant="outlined" startIcon={<ArrowBackIcon />} sx={actionButtonSx}>
                                Voltar
                            </Button>
                        )}
                        <Button to={primaryAction.to} size="large" variant="contained" component={RouterLink} startIcon={primaryAction.icon} sx={actionButtonSx}>
                            {primaryAction.label}
                        </Button>
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
}
